/**
 * FeeValidator – keeps fee items (customization fees) in sync with their parent line items.
 *
 * Responsibilities:
 * - When a customized parent line declares `_fee_variant_ids`, ensure corresponding fee items exist.
 * - Remove orphaned fee items when their parent is removed or no longer expects that fee.
 * - Keep fee item quantities in sync with their parent line quantities.
 *
 * Uses:
 * - /cart.js to read the current cart
 * - /cart/add.js to add missing fee lines
 * - /cart/change.js to adjust or remove existing fee lines
 *
 * Identifiers:
 * - Parent lines: cart items with properties._fee_variant_ids (comma-separated variant ids),
 *   and NOT marked as fee lines themselves.
 * - Fee lines: cart items with properties._is_fee_for_customization === "true".
 * - Optional: fee lines may carry `_fee_parent_key` to directly link to their parent line key.
 */
(function () {
  if (window.FeeValidator) return;

  class FeeValidator {
    constructor() {
      this.isUpdating = false;
      this.pending = false;
      this.init();
    }

    init() {
      const handler = () => this.scheduleCheck();
      document.addEventListener("product:added", handler);
      document.addEventListener("product:changed", handler);
      document.addEventListener("cart:updated", handler);

      this.scheduleCheck();
    }

    scheduleCheck() {
      if (this.isUpdating) {
        this.pending = true;
        return;
      }
      this.checkFees();
    }

    async checkFees() {
      this.isUpdating = true;
      this.pending = false;

      try {
        const res = await fetch("/cart.js", {
          credentials: "same-origin",
          headers: { "Accept": "application/json" },
        });
        if (!res.ok) {
          throw new Error("Failed to load cart");
        }
        const cart = await res.json();
        await this.syncFees(cart);
      } catch (e) {
        console.warn("FeeValidator: failed to validate fees", e);
      } finally {
        this.isUpdating = false;
        if (this.pending) {
          this.scheduleCheck();
        }
      }
    }

    /**
     * Build maps of parent and fee lines, then compute and apply corrections.
     * @param {{ items: Array }} cart
     */
    async syncFees(cart) {
      const items = Array.isArray(cart.items) ? cart.items : [];

      const parents = [];
      const fees = [];

      for (const item of items) {
        const props = item.properties || {};
        const isFee = props._is_fee_for_customization === "true";
        const feeIdsRaw = props._fee_variant_ids;
        if (!isFee && feeIdsRaw) {
          const ids = String(feeIdsRaw)
            .split(",")
            .map((v) => parseInt(v, 10))
            .filter((id) => Number.isFinite(id) && id > 0);
          if (ids.length > 0) {
            parents.push({
              key: item.key,
              quantity: item.quantity || 1,
              variantId: item.variant_id,
              feeVariantIds: ids,
              item,
            });
          }
        } else if (isFee) {
          fees.push({
            key: item.key,
            quantity: item.quantity || 1,
            variantId: item.variant_id,
            parentKey: props._fee_parent_key || null,
            item,
          });
        }
      }

      const changes = [];
      const adds = [];

      // Helper: find fee lines for a given parent and fee variant
      const findFeeLinesFor = (parentKey, feeVariantId) =>
        fees.filter(
          (fee) =>
            fee.variantId === feeVariantId &&
            (!fee.parentKey || fee.parentKey === parentKey)
        );

      // 1) For each parent, ensure required fees exist and are in sync.
      for (const parent of parents) {
        for (const feeId of parent.feeVariantIds) {
          const relatedFees = findFeeLinesFor(parent.key, feeId);

          if (relatedFees.length === 0) {
            // Missing fee: plan to add it.
            adds.push({
              id: feeId,
              quantity: parent.quantity,
              properties: {
                _is_fee_for_customization: "true",
                _fee_parent_key: parent.key,
              },
            });
            continue;
          }

          // If we have multiple fee lines for the same fee/parent, keep the first, remove the rest.
          const [primary, ...duplicates] = relatedFees;
          for (const dup of duplicates) {
            changes.push({
              id: dup.key,
              quantity: 0,
            });
          }

          // Ensure primary has correct quantity and parent key.
          const newProps = Object.assign(
            {},
            primary.item.properties || {},
            {
              _is_fee_for_customization: "true",
              _fee_parent_key: parent.key,
            }
          );
          if (
            primary.quantity !== parent.quantity ||
            primary.parentKey !== parent.key
          ) {
            changes.push({
              id: primary.key,
              quantity: parent.quantity,
              properties: newProps,
            });
          }
        }
      }

      // 2) Remove orphaned fee lines (no matching parent / fee relationship).
      for (const fee of fees) {
        const props = fee.item.properties || {};
        const parentKey = props._fee_parent_key || fee.parentKey;
        if (!parentKey) {
          // If we cannot associate this fee to any parent, consider it orphaned.
          if (!parents.length) {
            changes.push({ id: fee.key, quantity: 0 });
          }
          continue;
        }
        const parent = parents.find((p) => p.key === parentKey);
        if (!parent) {
          // Parent removed: remove fee.
          changes.push({ id: fee.key, quantity: 0 });
          continue;
        }
        if (!parent.feeVariantIds.includes(fee.variantId)) {
          // Parent no longer expects this fee.
          changes.push({ id: fee.key, quantity: 0 });
        }
      }

      // Apply operations sequentially to avoid conflicting cart writes.
      for (const add of adds) {
        await this.addFeeItem(add);
      }
      for (const change of changes) {
        await this.changeLine(change);
      }
    }

    async addFeeItem(add) {
      try {
        await fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: [add],
          }),
        });
      } catch (e) {
        console.warn("FeeValidator: failed to add fee item", e);
      }
    }

    async changeLine(change) {
      try {
        await fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(change),
        });
      } catch (e) {
        console.warn("FeeValidator: failed to change line", e);
      }
    }
  }

  window.FeeValidator = FeeValidator;

  function initFeeValidator() {
    try {
      // eslint-disable-next-line no-new
      new FeeValidator();
    } catch (e) {
      console.warn("FeeValidator: init failed", e);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFeeValidator);
  } else {
    initFeeValidator();
  }
})();

